import FormDatePicker from '@/components/form-date-picker';
import commonMessages from '@/lang/messages/common';
import { useTracking } from '@/libs/analytics';
import {
    ageToAnalyticsAgeRange,
    genderToAnalyticsGender,
} from '@/libs/analytics/transformers';
import {
    onAnalyticsClickHandler,
    ButtonAnalyticsProps,
    OnAnalyticsClickHandler,
} from '@/libs/analytics/with-analytics';
import { sxAppend } from '@/libs/material/sx';
import { getUtcDateForMDY, getYMD, yearsAgo } from '@/utils/date-time';
import {
    EnumButtonName,
    EnumScreenName,
    EventProperties,
} from '@calm/evexia-analytics';
import { AccountAccount } from '@calm/evexia-web-sdk';
import useAccount from '@rhg/hooks/use-account';
import useCircles from '@rhg/hooks/use-circles';
import { useIsMobile } from '@rhg/hooks/use-is-mobile';
import { useLogoutConfirmation } from '@rhg/hooks/use-logout';
import { useUpdateAccount } from '@rhg/hooks/use-update-account';
import { logger } from '@rhg/libs/logger';
import {
    Box,
    Button,
    Theme,
    Typography,
    SxProps,
    Stack,
} from '@rhg/libs/material';
import {
    RefObject,
    ReactNode,
    useCallback,
    useId,
    useMemo,
    useRef,
    useState,
} from 'react';
import { FieldErrors, useWatch } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { LoadingDots } from '../animations/loading-dots';
import { Heading } from '../heading';
import { AgeGenderFormActions } from './age-gender-form-actions';
import { AgeGenderFormIcon } from './age-gender-form-icon';
import {
    AgeGenderFormState,
    useAgeAndGenderFormContext,
    AgeAndGenderFormProvider,
} from './age-gender-form-provider';
import { GenderSelect } from './gender-select';
import { ageGenderMessages } from './messages';
import {
    styleAddEditButtonGroup,
    styleAddEditButton,
    styleContainer,
    styleContent,
    styleCopyBlock,
    styleForm,
    styleOuterWrapper,
    styleFormOuterWrapper,
    styleVerticalSpacing,
    styleFormIconWrapper,
} from './styles';
import { UnderageDialog } from './underage-dialog';

interface AgeGenderProps {
    title?: string;
    heading?: string;
    subHeading?: string;
    sx?: SxProps<Theme>;
    primaryCtaLabel?: string;
    primaryCtaStartIcon?: ReactNode;
    secondaryCtaLabel?: string;
    showSecondaryCta?: boolean;
    onConfirm?: () => void;
    onCancel?: () => void;
    showAddEditButtons?: boolean;
    trackProps?: EventProperties;
    trackScreen?: EnumScreenName;
}

function AgeGenderStandaloneContent({
    title,
    heading,
    subHeading,
    sx,
    primaryCtaLabel,
    primaryCtaStartIcon,
    secondaryCtaLabel,
    showSecondaryCta = true,
    showAddEditButtons = false,
    onConfirm,
    onCancel,
    trackProps,
    trackScreen,
    trackSubmitProps,
}: AgeGenderProps & { trackSubmitProps?: EventProperties }) {
    const formId = useId();
    const { formatMessage } = useIntl();
    const isSubmitDisabledRef = useRef(false);

    return (
        <Box sx={sxAppend(styleOuterWrapper, sx)}>
            <Box sx={sxAppend(styleContainer)}>
                {showAddEditButtons ? (
                    <Box sx={styleAddEditButtonGroup}>
                        <Button
                            sx={styleAddEditButton}
                            onClick={onCancel}
                            trackButton="Cancel"
                            trackScreen={trackScreen}
                            trackProps={trackProps}
                        >
                            {formatMessage(commonMessages['common.cancel'])}
                        </Button>
                        <Typography variant="h5">
                            {title ?? formatMessage(ageGenderMessages.title)}
                        </Typography>
                        <Button
                            sx={styleAddEditButton}
                            type="submit"
                            form={formId}
                            disabled={isSubmitDisabledRef.current}
                            trackButton="SubmitAgeAndGender"
                            trackScreen={trackScreen}
                            trackProps={trackProps}
                        >
                            {formatMessage(commonMessages['common.save'])}
                        </Button>
                    </Box>
                ) : (
                    <Typography variant="h5">
                        {title ?? formatMessage(ageGenderMessages.title)}
                    </Typography>
                )}
                <Box sx={styleFormIconWrapper}>
                    <AgeGenderFormIcon />
                </Box>
                <AgeGenderForm
                    heading={heading}
                    subHeading={subHeading}
                    onConfirm={onConfirm}
                    formId={formId}
                    isDisabledRef={isSubmitDisabledRef}
                    sx={sx}
                    trackProps={trackProps}
                    trackScreen={trackScreen}
                />
            </Box>
            <AgeGenderFormActions
                formId={formId}
                primaryCtaLabel={primaryCtaLabel}
                primaryCtaStartIcon={primaryCtaStartIcon}
                secondaryCtaLabel={secondaryCtaLabel}
                showSecondaryCta={showSecondaryCta}
                trackProps={trackProps}
                trackSubmitProps={trackSubmitProps}
                trackScreen={trackScreen}
                onCancel={onCancel}
                isDisabled={isSubmitDisabledRef.current}
            />
        </Box>
    );
}
function AgeGenderStandaloneWithContext(props: AgeGenderProps) {
    const { control } = useAgeAndGenderFormContext();
    const [dob, gender] = useWatch({ control, name: ['dob', 'gender'] });

    const trackSubmitProps = useMemo<EventProperties>(
        () => ({
            ...props.trackProps,
            AgeRange: ageToAnalyticsAgeRange(dob),
            Gender: genderToAnalyticsGender(gender),
        }),
        [props.trackProps, dob, gender]
    );

    return (
        <AgeGenderStandaloneContent
            {...props}
            trackSubmitProps={trackSubmitProps}
        />
    );
}

export function AgeGenderStandalone(props: AgeGenderProps) {
    const { account } = useAccount();

    return (
        <AgeAndGenderFormProvider account={account}>
            <AgeGenderStandaloneWithContext {...props} />
        </AgeAndGenderFormProvider>
    );
}

interface AgeGenderFormProps {
    /** formId must be passed to the AgeGenderFormActions component to ensure the form is submitted when the primary CTA is clicked */
    formId: string;
    /** Needed to properly disable the form CTA(s) when form is submitted */
    isDisabledRef: RefObject<boolean>;
    heading?: string;
    subHeading?: string;
    formIcon?: ReactNode;
    sx?: SxProps<Theme>;
    onConfirm?: (newAccount?: AccountAccount) => void;
    onDobOpen?: () => void;
    onGenderOpen?: () => void;
    onDobChange?: (value: unknown) => void;
    onGenderChange?: (value: unknown) => void;
    willForceOver18?: boolean;
    trackProps?: EventProperties;
    trackScreen?: EnumScreenName;
}

interface AgeGenderFormComponentProps
    extends Omit<AgeGenderFormProps, 'trackSource' | 'trackScreen'> {
    onSubmit: (account: Partial<AccountAccount>) => Promise<void>;
}

export const AgeGenderFormComponent = ({
    heading,
    subHeading,
    sx,
    onConfirm,
    formId,
    isDisabledRef,
    onSubmit,
    onDobOpen,
    onGenderOpen,
    onDobChange,
    onGenderChange,
}: AgeGenderFormComponentProps) => {
    const { formatMessage } = useIntl();
    const isMobile = useIsMobile();

    const { control, handleSubmit, setFocus } = useAgeAndGenderFormContext();

    const _onInvalid = useCallback(
        (errors: FieldErrors<AgeGenderFormState>) => {
            const [firstInvalidField] = Object.keys(
                errors
            ) as (keyof AgeGenderFormState)[];
            if (firstInvalidField) {
                setFocus(firstInvalidField);
            }
        },
        [setFocus]
    );

    const _onSubmit = useCallback(
        async (ageGenderForm: AgeGenderFormState) => {
            isDisabledRef.current = true;
            const hasDobChanged = control.getFieldState('dob').isDirty;
            const hasGenderChanged = control.getFieldState('gender').isDirty;

            const formattedDate = getYMD(ageGenderForm.dob);

            await onSubmit({
                ...(hasDobChanged ? { dob: formattedDate } : {}),
                ...(hasGenderChanged ? { gender: ageGenderForm.gender } : {}),
            });
            isDisabledRef.current = false;
            onConfirm?.();
        },
        [control, isDisabledRef, onConfirm, onSubmit]
    );

    return (
        <Box sx={sxAppend(styleFormOuterWrapper, sx)}>
            <Box sx={styleContent}>
                <Box sx={styleCopyBlock}>
                    <Heading
                        titleAlign="center"
                        subtitleAlign="center"
                        titleVariant={isMobile ? 'h5' : 'h4'}
                        title={
                            heading ?? formatMessage(ageGenderMessages.heading)
                        }
                        subtitleVariant="body1"
                        subtitle={
                            subHeading ??
                            formatMessage(ageGenderMessages.subHeading)
                        }
                    />
                </Box>

                <form
                    onSubmit={(e) => {
                        handleSubmit(
                            _onSubmit,
                            _onInvalid
                        )(e).catch((err) =>
                            logger.error(
                                'Failed to submit age and gender form',
                                {},
                                err as Error
                            )
                        );
                    }}
                    style={styleForm}
                    id={formId}
                    data-testid="age-gender-form"
                >
                    <FormDatePicker
                        name="dob"
                        label={formatMessage(ageGenderMessages.dobInputLabel)}
                        control={control}
                        datePickerProps={{
                            onOpen: onDobOpen,
                            onAccept: onDobChange,
                        }}
                        textFieldProps={{
                            fullWidth: true,
                        }}
                    />
                    <GenderSelect
                        control={control}
                        sx={styleVerticalSpacing}
                        onOpen={onGenderOpen}
                        onChange={onGenderChange}
                    />
                </form>
            </Box>
        </Box>
    );
};

export const AgeGenderForm = ({
    heading,
    subHeading,
    sx,
    onConfirm,
    formId,
    isDisabledRef,
    willForceOver18,
    trackProps,
    trackScreen,
}: AgeGenderFormProps) => {
    const { isFetched } = useAccount();
    const { updateAccountAsync, reset: accountReset } = useUpdateAccount();
    const { refetch: refetchCircles } = useCircles();
    const [showNotAvailableDialog, setShowNotAvailableDialog] = useState(false);
    const { openLogoutDialog } = useLogoutConfirmation();
    const { track, state } = useTracking();

    const onSubmit = useCallback(
        async (accountChanges: Partial<AccountAccount>) => {
            if (willForceOver18) {
                const dobString = accountChanges.dob;
                if (!dobString) {
                    setShowNotAvailableDialog(true);
                    return;
                }

                const dobDate = getUtcDateForMDY(dobString);
                if (yearsAgo(dobDate) < 18) {
                    setShowNotAvailableDialog(true);
                    return;
                }
            }

            try {
                const newAccount = await updateAccountAsync(accountChanges);
                await refetchCircles();
                onConfirm?.(newAccount);
            } catch (error: unknown) {
                if (
                    typeof error === 'object' &&
                    error !== null &&
                    'status' in error &&
                    error.status === 422
                ) {
                    // Restricted due to user's age
                    setShowNotAvailableDialog(true);
                }
            }
        },
        [willForceOver18, onConfirm, refetchCircles, updateAccountAsync]
    );

    const sharedAnalyticsProps = useMemo<
        Omit<ButtonAnalyticsProps<OnAnalyticsClickHandler>, 'trackButton'>
    >(
        () => ({
            track,
            currentFeature: state.currentFeature,
            currentScreenName: trackScreen ?? state.currentScreenName,
            trackProps,
        }),
        [
            state.currentFeature,
            state.currentScreenName,
            track,
            trackProps,
            trackScreen,
        ]
    );

    const trackDobPicker = useCallback(() => {
        onAnalyticsClickHandler({
            trackButton: EnumButtonName.DateOfBirthDropdown,
            ...sharedAnalyticsProps,
        });
    }, [sharedAnalyticsProps]);

    const trackGenderPicker = useCallback(() => {
        onAnalyticsClickHandler({
            trackButton: EnumButtonName.GenderDropdown,
            ...sharedAnalyticsProps,
        });
    }, [sharedAnalyticsProps]);

    const trackDobSelect = useCallback(() => {
        onAnalyticsClickHandler({
            trackButton: EnumButtonName.SelectDateOfBirth,
            ...sharedAnalyticsProps,
        });
    }, [sharedAnalyticsProps]);

    const trackGenderSelect = useCallback(() => {
        onAnalyticsClickHandler({
            trackButton: EnumButtonName.SelectGender,
            ...sharedAnalyticsProps,
        });
    }, [sharedAnalyticsProps]);

    if (!isFetched) {
        return (
            <Stack height={200} alignItems="center" justifyContent="center">
                <LoadingDots />
            </Stack>
        );
    }

    const onLogout = () => {
        accountReset();
        openLogoutDialog().catch((err) =>
            logger.error('Failed to open logout dialog', {}, err as Error)
        );
    };

    return (
        <>
            {showNotAvailableDialog && (
                <UnderageDialog
                    trackScreen={trackScreen}
                    trackProps={trackProps}
                    onClose={() => setShowNotAvailableDialog(false)}
                    onLogout={onLogout}
                />
            )}
            <AgeGenderFormComponent
                heading={heading}
                subHeading={subHeading}
                sx={sx}
                formId={formId}
                isDisabledRef={isDisabledRef}
                onSubmit={onSubmit}
                onDobOpen={trackDobPicker}
                onGenderOpen={trackGenderPicker}
                onDobChange={trackDobSelect}
                onGenderChange={trackGenderSelect}
            />
        </>
    );
};
